import React from "react";
import { useLang } from "../../context/LanguageContext";
import { useCmsBlocks } from "../../lib/useCmsBlocks";

/**
 * En riktig bokning, från förfrågan till betald.
 *
 * **Sektionen renderas bara när det finns en verklig bokning att visa.**
 * Designunderlaget levererade den färdigifylld med en namngiven brud, ett citat,
 * ett slott och ett belopp. Ingenting av det har hänt. Ett påhittat omdöme med
 * ett namn under är en falsk recension, och det är olagligt enligt
 * marknadsföringslagen och EU:s direktiv om otillbörliga affärsmetoder, oavsett
 * hur representativt exemplet känns.
 *
 * Lösningen är inte att stryka sektionen, för den är den starkaste på hela
 * sidan: en tidslinje som visar att det gick på ett par dagar säger mer än tre
 * trygghetskort. Lösningen är att den fylls i från admin när den första kunden
 * gett sitt samtycke. Tills dess finns den inte, och sidan hänger ihop ändå.
 *
 * Blocken som krävs, alla under ``home.example.*``:
 *
 *   customer_name, customer_role, quote, talent_name, talent_role,
 *   event_type, event_date, venue, guests, duration, amount, payout_date
 *
 * Saknas namnet eller citatet ritas ingenting. Att visa en halv tidslinje utan
 * någon som står bakom den vore samma påstående med mindre text.
 */

const T = {
  sv: {
    eyebrow: "En bokning som redan hänt",
    booked: "Bekräftad bokning",
    event: "Event", date: "Datum", venue: "Plats", guests: "Gäster",
    duration: "Speltid", total: "Totalt betalt",
    holdTitle: "Betalningen hålls till efter eventet",
    holdBody: "Utbetalas till talangen",
  },
  en: {
    eyebrow: "A booking that already happened",
    booked: "Confirmed booking",
    event: "Event", date: "Date", venue: "Venue", guests: "Guests",
    duration: "Set length", total: "Total paid",
    holdTitle: "Payment is held until after the event",
    holdBody: "Paid out to the talent",
  },
};

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 text-[13.5px]">
    <span className="text-neutral-500">{label}</span>
    <span className="text-neutral-200 text-right">{value}</span>
  </div>
);

const BookingExample = () => {
  const { lang } = useLang();
  const t = T[lang === "sv" ? "sv" : "en"];
  const b = useCmsBlocks();
  const g = (k) => b[`home.example.${k}`];

  // Utan en verklig kund och ett verkligt citat finns ingen sektion.
  if (!g("customer_name") || !g("quote")) return null;

  const steps = [g("step1"), g("step2"), g("step3"), g("step4")].filter(Boolean);

  return (
    <section className="border-t border-[#1e1e1e]" data-testid="booking-example">
      <div className="max-w-[1000px] mx-auto px-6 py-20 md:py-24">
        <div className="text-center mb-12">
          <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-3">{t.eyebrow}</div>
          {g("headline") && (
            <h2 className="text-[32px] md:text-[42px] font-extrabold tracking-tight text-white leading-[1.1] max-w-[800px] mx-auto">
              {g("headline")}
            </h2>
          )}
        </div>

        <div className="rounded-2xl border border-[#2A2A2A] bg-[#181818] overflow-hidden grid md:grid-cols-2">

          <div className="p-8 md:p-10 md:border-r border-[#2A2A2A]">
            <div className="flex items-center gap-3.5 mb-6">
              {g("customer_avatar")
                ? <img src={g("customer_avatar")} alt="" className="w-12 h-12 rounded-full object-cover" />
                : <div className="w-12 h-12 rounded-full bg-[#232326] flex items-center justify-center text-neutral-500 text-[15px] font-semibold">
                    {g("customer_name").slice(0, 1)}
                  </div>}
              <div>
                <div className="text-white font-medium text-[14.5px]">{g("customer_name")}</div>
                {g("customer_role") && <div className="text-[12px] text-neutral-500">{g("customer_role")}</div>}
              </div>
            </div>

            <blockquote className="text-[18px] md:text-[19px] leading-relaxed text-neutral-200 mb-7">
              {g("quote")}
            </blockquote>

            {steps.length > 0 && (
              <div className="space-y-3">
                {steps.map((s, i) => (
                  <div key={i} className="flex items-center gap-3 text-[13.5px]">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${
                      i === steps.length - 1 ? "bg-[#1A8A4A]" : "bg-[#3A3A3A]"}`} />
                    <span className={i === steps.length - 1 ? "text-[#4ade80]" : "text-neutral-400"}>{s}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="p-8 md:p-10 bg-[#1A8A4A]/[0.04]">
            <div className="flex items-center justify-between mb-5">
              <span className="text-[11px] uppercase tracking-wider text-[#4ade80]">{t.booked}</span>
              {g("ref") && <span className="text-[11px] text-neutral-600">{g("ref")}</span>}
            </div>

            {g("talent_name") && (
              <div className="flex items-center gap-3.5 pb-5 mb-5 border-b border-[#2A2A2A]">
                {g("talent_avatar")
                  ? <img src={g("talent_avatar")} alt="" className="w-11 h-11 rounded-full object-cover" />
                  : <div className="w-11 h-11 rounded-full bg-[#232326]" />}
                <div>
                  <div className="text-white font-semibold text-[15px]">{g("talent_name")}</div>
                  {g("talent_role") && <div className="text-[12px] text-neutral-500">{g("talent_role")}</div>}
                </div>
              </div>
            )}

            <div className="space-y-2.5 mb-6">
              {g("event_type") && <Row label={t.event} value={g("event_type")} />}
              {g("event_date") && <Row label={t.date} value={g("event_date")} />}
              {g("venue") && <Row label={t.venue} value={g("venue")} />}
              {g("guests") && <Row label={t.guests} value={g("guests")} />}
              {g("duration") && <Row label={t.duration} value={g("duration")} />}
              {g("amount") && (
                <div className="flex justify-between items-baseline gap-4 pt-3 border-t border-[#2A2A2A]">
                  <span className="text-[13.5px] text-neutral-400">{t.total}</span>
                  <span className="text-[20px] font-bold text-white tabular-nums">{g("amount")}</span>
                </div>
              )}
            </div>

            <div className="rounded-lg border border-[#2A2A2A] bg-[#0D0D0D] p-3.5 flex items-start gap-3">
              <span className="w-2 h-2 rounded-full bg-[#4ade80] shrink-0 mt-1.5" />
              <div className="text-[12.5px]">
                <div className="text-neutral-200">{t.holdTitle}</div>
                {g("payout_date") && (
                  <div className="text-neutral-500 mt-0.5">{t.holdBody} {g("payout_date")}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookingExample;
